import { useEffect, useRef, useState } from 'preact/hooks'
import { AlertCircle, ChevronRight, Eye, LayoutTemplate, Pencil, Trash2 } from 'lucide-preact'
import { usePageHeader } from '@/hooks/usePageHeader'
import { useCompany } from '@/hooks/useCompany'
import { useToast } from '@/hooks/useToast'
import { useLandingPageTemplates } from '@/hooks/useLandingPageTemplates'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { BRAND_CLASSES } from '@/constants/theme'
import { fieldLimits } from '@/types/field-limits.generated'
import { routes } from '@/utils/routes'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import {
    GHOST_SMALL,
    QUIET_BUTTON,
    SAVE_SMALL,
    SECONDARY_BUTTON,
    SECONDARY_SMALL,
    SQUARE_BADGE,
} from '@/components/knowledgeBase/brandGuidelines/brandGuidelinesStyles'
import { TemplatePreviewOverlay } from './TemplatePreviewOverlay'
import { TemplateThumbnail } from './TemplateThumbnail'
import { groupByWebType, savedText, signedOffText } from './templateText'

const GROUP_HEADING = 'px-6 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400'
const QUIET_DANGER = 'inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500'
const DANGER_SMALL = 'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed'

interface TemplatesPageProps {
    clientCompanyId: string
    workspaceId: string
}

/** Which row is being renamed or asked about removal. One at a time: opening one closes the other. */
type RowEdit = { webId: string; mode: 'rename' | 'remove' }

/**
 * The workspace's landing-page templates (ORB-1103): what each one copies, where it came from, and Preview, Rename and
 * Remove. Removing a template leaves its landing page as it is. Editors only, like the list it reads: the nav hides the
 * link from a viewer, and one who follows a link here is told why rather than shown a read that can never succeed.
 */
export function TemplatesPage({ clientCompanyId, workspaceId }: TemplatesPageProps) {
    const { setHeader } = usePageHeader()
    const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)

    useEffect(() => {
        setHeader({ items: [{ text: 'Templates' }] })
    }, [])

    if (!canEdit) {
        return (
            <div className="p-6">
                <div className="max-w-[960px] flex flex-col gap-2 py-16">
                    <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Templates</h1>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Using templates needs edit rights</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        You can read this workspace’s campaigns. An admin can give you edit rights.
                    </p>
                </div>
            </div>
        )
    }

    return <TemplatesList clientCompanyId={clientCompanyId} workspaceId={workspaceId} />
}

function TemplatesList({ clientCompanyId, workspaceId }: TemplatesPageProps) {
    const { workspaces } = useCompany()
    const { showSuccess } = useToast()
    const { templates, error, reload, renameTemplate, removeTemplate } = useLandingPageTemplates(workspaceId)
    const [previewing, setPreviewing] = useState<LandingPageTemplateListItemDto | null>(null)
    const [edit, setEdit] = useState<RowEdit | null>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)

    const workspaceName = workspaces.find(workspace => workspace.Id === workspaceId)?.Name ?? 'this workspace'
    const scoped = routes.scoped(clientCompanyId, workspaceId)

    if (!templates && error) {
        return (
            <div className="p-6">
                <div role="alert" className="flex flex-col items-start gap-3 py-16">
                    <p className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" aria-hidden="true" />
                        The templates could not load.
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
                    <button type="button" className={SECONDARY_BUTTON} onClick={() => void reload()}>Try again</button>
                </div>
            </div>
        )
    }

    if (!templates) return <TemplatesSkeleton />

    const remove = async (template: LandingPageTemplateListItemDto) => {
        if (!await removeTemplate(template.WebId)) return
        setEdit(null)
        showSuccess('Template removed', `“${template.Template.Name}” is no longer a template. The landing page itself isn’t changed.`)
        // The row and its buttons are gone, so focus would fall to the page body.
        headingRef.current?.focus()
    }

    return (
        <div className="p-6">
            <div className="max-w-[960px] flex flex-col gap-7">
                <div>
                    <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white focus:outline-none">
                        Templates
                    </h1>
                    <p className="mt-1 max-w-[64ch] text-sm text-slate-600 dark:text-slate-300">
                        Landing pages saved as templates in {workspaceName}. When you build a new landing page, start from
                        one of these and Webster swaps in that web’s details. New pages always copy the template’s latest
                        signed-off version.
                    </p>
                </div>

                <section aria-label="Templates" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden pb-3">
                    {templates.length === 0 ? (
                        <div className="flex flex-col items-center text-center px-6 pt-16 pb-12">
                            <LayoutTemplate className="w-10 h-10 mb-3 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                            <p className="text-sm font-medium text-slate-900 dark:text-white">No templates yet</p>
                            <p className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
                                Once a landing page is signed off, open its web’s Landing Page tab and choose Save as
                                template. It then shows up here, ready to start new landing pages from.
                            </p>
                        </div>
                    ) : groupByWebType(templates).map((group, index) => {
                        const headingId = `templates-group-${index}`
                        return (
                            <section key={group.webType} aria-labelledby={headingId}>
                                <h2 id={headingId} className={`${GROUP_HEADING} ${index === 0 ? 'pt-6' : 'pt-8'}`}>{group.webType}</h2>
                                <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {group.templates.map(template => (
                                        <li key={template.WebId} className="mx-6">
                                            <TemplateRow
                                                workspaceId={workspaceId}
                                                template={template}
                                                webHref={`${scoped.web(template.CampaignId, template.WebId)}/landing-page`}
                                                mode={edit?.webId === template.WebId ? edit.mode : null}
                                                onEdit={mode => setEdit(mode ? { webId: template.WebId, mode } : null)}
                                                onPreview={() => setPreviewing(template)}
                                                onRename={name => renameTemplate(template.WebId, name)}
                                                onRemove={() => remove(template)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )
                    })}
                </section>
            </div>

            {previewing && (
                <TemplatePreviewOverlay workspaceId={workspaceId} template={previewing} onClose={() => setPreviewing(null)} />
            )}
        </div>
    )
}

interface TemplateRowProps {
    workspaceId: string
    template: LandingPageTemplateListItemDto
    webHref: string
    mode: RowEdit['mode'] | null
    onEdit: (mode: RowEdit['mode'] | null) => void
    onPreview: () => void
    /** Resolves false when the rename was refused; the reason has already been shown. */
    onRename: (name: string) => Promise<boolean>
    onRemove: () => Promise<void>
}

function TemplateRow({ workspaceId, template, webHref, mode, onEdit, onPreview, onRename, onRemove }: TemplateRowProps) {
    const [name, setName] = useState(template.Template.Name)
    const [busy, setBusy] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const renameRef = useRef<HTMLButtonElement>(null)
    const removeRef = useRef<HTMLButtonElement>(null)
    const keepRef = useRef<HTMLButtonElement>(null)
    const rowRef = useRef<HTMLDivElement>(null)
    // Where focus goes back to when the inline form or confirmation closes: the button that opened it.
    const previousModeRef = useRef(mode)

    useEffect(() => {
        const previous = previousModeRef.current
        previousModeRef.current = mode
        if (mode === 'rename') {
            setName(template.Template.Name)
            inputRef.current?.focus()
            inputRef.current?.select()
            return
        }
        if (mode === 'remove') {
            keepRef.current?.focus()
            return
        }
        // Opening another row closes this one in the same render, and focus has moved there: leave it.
        const active = document.activeElement
        if (active !== document.body && !rowRef.current?.contains(active)) return
        if (previous === 'rename') renameRef.current?.focus()
        else if (previous === 'remove') removeRef.current?.focus()
    }, [mode])

    const trimmed = name.trim()
    const saveRename = async () => {
        if (!trimmed || busy) return
        if (trimmed === template.Template.Name) {
            onEdit(null)
            return
        }
        setBusy(true)
        const renamed = await onRename(trimmed)
        setBusy(false)
        if (renamed) onEdit(null)
    }

    const confirmRemove = async () => {
        setBusy(true)
        await onRemove()
        setBusy(false)
    }

    const version = template.LatestSignedOffVersion
    // The preview is refused for the same reasons the template can't be used, so a template with one has nothing to show.
    const previewable = !!version && !template.CannotUseReason
    const inputId = `template-name-${template.WebId}`

    return (
        <div ref={rowRef} className="py-5 grid grid-cols-1 sm:grid-cols-[160px_minmax(0,1fr)] lg:grid-cols-[160px_minmax(0,1fr)_auto] gap-x-6 gap-y-4 items-start">
            <span className="hidden sm:block">
                <TemplateThumbnail workspaceId={workspaceId} template={template} hasPage={previewable} size="list" />
            </span>

            <div className="min-w-0">
                {mode === 'rename' ? (
                    <div className="flex items-center gap-2">
                        <label htmlFor={inputId} className="sr-only">Template name</label>
                        <input
                            ref={inputRef}
                            id={inputId}
                            type="text"
                            value={name}
                            autoComplete="off"
                            maxLength={fieldLimits.LandingPageTemplate.Name.MaxLength}
                            disabled={busy}
                            onInput={event => setName((event.target as HTMLInputElement).value)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    void saveRename()
                                } else if (event.key === 'Escape') {
                                    event.preventDefault()
                                    onEdit(null)
                                }
                            }}
                            className={`flex-1 min-w-0 max-w-[360px] text-sm px-3 py-1.5 rounded-md border ${BRAND_CLASSES.INPUT.NORMAL} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50`}
                        />
                        <button type="button" className={SAVE_SMALL} disabled={!trimmed || busy} onClick={() => void saveRename()}>Save</button>
                        <button type="button" className={GHOST_SMALL} disabled={busy} onClick={() => onEdit(null)}>Cancel</button>
                    </div>
                ) : (
                    <p className="text-sm font-medium text-slate-900 dark:text-white break-words">{template.Template.Name}</p>
                )}
                <p className="mt-1 flex flex-wrap items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                    <span>{template.CampaignName}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                    <a href={webHref} className="hover:text-slate-700 dark:hover:text-slate-200 hover:underline underline-offset-2">{template.WebName}</a>
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
                    {template.CannotUseReason ? (
                        <span className="inline-flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                            <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
                            <span>Can’t be used right now. {template.CannotUseReason}</span>
                        </span>
                    ) : version ? (
                        <span className={SQUARE_BADGE}>Uses {signedOffText(version)}</span>
                    ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">Nothing signed off</span>
                    )}
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{savedText(template)}</p>
            </div>

            <div className="sm:col-start-2 lg:col-start-auto lg:min-w-[250px] flex lg:justify-end">
                {mode === 'remove' ? (
                    <div
                        role="group"
                        aria-label="Confirm removal"
                        onKeyDown={event => { if (event.key === 'Escape') onEdit(null) }}
                        className="flex flex-col items-start lg:items-end lg:text-right max-w-[280px]"
                    >
                        <p className="text-sm font-medium text-slate-900 dark:text-white">Remove this template?</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">The landing page itself isn’t changed.</p>
                        <div className="mt-2.5 flex gap-2">
                            <button
                                ref={keepRef}
                                type="button"
                                className={GHOST_SMALL}
                                disabled={busy}
                                onClick={() => onEdit(null)}
                            >
                                Keep
                            </button>
                            <button type="button" className={DANGER_SMALL} disabled={busy} onClick={() => void confirmRemove()}>
                                Remove template
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`flex items-center gap-1 ${mode === 'rename' ? 'invisible' : ''}`}>
                        <button
                            type="button"
                            className={`${SECONDARY_SMALL} mr-1`}
                            disabled={!previewable}
                            title={previewable ? undefined : template.CannotUseReason ?? 'Nothing signed off to preview'}
                            onClick={onPreview}
                        >
                            <Eye className="w-4 h-4" aria-hidden="true" />
                            Preview
                        </button>
                        <button ref={renameRef} type="button" className={QUIET_BUTTON} onClick={() => onEdit('rename')}>
                            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                            Rename
                        </button>
                        <button ref={removeRef} type="button" className={QUIET_DANGER} onClick={() => onEdit('remove')}>
                            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                            Remove
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

/** The page's own footprint while it loads, so nothing jumps when the rows arrive. */
function TemplatesSkeleton() {
    const block = 'bg-shimmer-light animate-shimmer rounded-md'
    return (
        <div className="p-6">
            <div aria-busy="true" className="max-w-[960px] flex flex-col gap-7">
                <span className="sr-only" role="status">Loading the templates</span>
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Templates</h1>
                    <span className={`${block} h-4 w-96 max-w-full`} />
                </div>
                <div aria-hidden="true" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden px-6 pt-6 pb-3">
                    <span className={`${block} block h-3 w-24 mb-2`} />
                    <div className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700">
                        {[0, 1, 2].map(index => (
                            <div key={index} className="py-5 grid grid-cols-[160px_minmax(0,1fr)] gap-x-6">
                                <span className={`${block} h-[100px]`} />
                                <span className="flex flex-col gap-2">
                                    <span className={`${block} h-4 w-56`} />
                                    <span className={`${block} h-4 w-72 max-w-full`} />
                                    <span className={`${block} h-6 w-64 max-w-full`} />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
